// features/sidebar-order.js

// Sidebar order for one role. Each module's `order` (from get-by-role) is its
// position among its siblings for this role: the role's top-level modules, or
// the sub-modules under one parent. The backend (role_module_mapping/controller.js)
// follows the same rules: a newly assigned module goes last in its group, in
// module id order.
//
// An "arrangement" is { [groupKey]: [moduleId, ...] }: the order the user has
// set for each group. Modules assigned since then are appended when the tree is built.

export const groupKey = (parentFid) => (parentFid ? String(parentFid) : "root");

// By order (unordered last), then name, then id.
export const compareModules = (a, b) => {
  const aOrder = a.order ?? Number.MAX_SAFE_INTEGER;
  const bOrder = b.order ?? Number.MAX_SAFE_INTEGER;
  return aOrder - bOrder || a.name.localeCompare(b.name) || a.id - b.id;
};

// The saved order of the modules the role had when the dialog opened.
export const savedArrangement = (index, perms) => {
  const arrangement = {};
  const add = (key, ids) => {
    arrangement[key] = ids
      .filter((id) => perms[id]?.can_view)
      .map((id) => index.byId[id])
      .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER) || a.id - b.id)
      .map((m) => m.id);
  };
  add("root", index.roots);
  index.roots.forEach((id) => add(groupKey(id), index.childrenById[id]));
  return arrangement;
};

// One group's assigned modules in order: arranged ones first, then newly assigned ones by id.
const orderGroup = (ids, arranged = [], perms) => {
  const assigned = ids.filter((id) => perms[id]?.can_view);
  const assignedSet = new Set(assigned);
  const kept = arranged.filter((id) => assignedSet.has(id));
  const keptSet = new Set(kept);
  return [...kept, ...assigned.filter((id) => !keptSet.has(id)).sort((a, b) => a - b)];
};

// The role's assigned modules as the sidebar will show them: [{ id, children: [id] }].
export const buildOrderTree = (index, perms, arrangement) =>
  orderGroup(index.roots, arrangement.root, perms).map((id) => ({
    id,
    children: orderGroup(index.childrenById[id], arrangement[groupKey(id)], perms),
  }));

// Module ids in sidebar order, as the save-order API takes them.
export const flattenOrderTree = (tree) => tree.flatMap((node) => [node.id, ...node.children]);

// Moves one module within its group to `toIndex`.
export const moveInGroup = (arrangement, key, ids, fromIndex, toIndex) => {
  const next = [...ids];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return { ...arrangement, [key]: next };
};

// Ids whose position differs between two trees, for the "unsaved" marker.
export const movedIds = (tree, baseTree) => {
  const positions = (t) => {
    const map = new Map();
    t.forEach((node, i) => {
      map.set(node.id, i);
      node.children.forEach((childId, j) => map.set(childId, j));
    });
    return map;
  };
  const current = positions(tree);
  const base = positions(baseTree);
  return new Set([...current.keys()].filter((id) => current.get(id) !== base.get(id)));
};
